import Stripe from "stripe";
import {toTwoDecimals} from "../../lib/picos/numberFormat";
import {addBusinessDays} from "date-fns";

class StripeInvoice {
    private _invoice: Stripe.Invoice;

    constructor(stripeInvoice: Stripe.Invoice) {
        this._invoice = stripeInvoice;
    }

    get amountPaid() {
        return this._invoice.amount_paid;
    }

    get amountDue() {
        return this._invoice.amount_due;
    }

    get dueDate() {
        return this._invoice.due_date ? new Date(this._invoice.due_date * 1000) : null;
    }

    get pdf() {
        return this._invoice.invoice_pdf;
    }

    get periodStart() {
        return this._invoice.period_start ? new Date(this._invoice.period_start * 1000) : null;
    }

    get periodEnd() {
        return this._invoice.period_end ? new Date(this._invoice.period_end * 1000) : null;
    }
}

class StripeSubscription {
    private _subscription: Stripe.Subscription;

    constructor(stripeSubscription: Stripe.Subscription) {
        this._subscription = stripeSubscription;

        // Testing only
        // console.log(this._subscription);
    }

    get id() {
        return this._subscription.id;
    }

    /**
     * Time at which the object was created. Measured in seconds since the Unix epoch.
     */
    get creationDate() {
        return new Date(this._subscription.start_date * 1000);
    }

    /**
     * End of the current period that the subscription has been invoiced for. At the end of this period, a new invoice will be created.
     */
    get currentPeriodEndDate() {
        return new Date(this._subscription.items.data[0].current_period_end * 1000);
    }

    /**
     * Start of the current period that the subscription has been invoiced for.
     */
    get currentPeriodStartDate() {
        return new Date(this._subscription.items.data[0].current_period_start * 1000);
    }

    get nextPeriodStartDate() {
        let date = this.currentPeriodEndDate;
        return addBusinessDays(date, 1);
    }

    /**
     * If the subscription has been canceled with the at_period_end flag set to true, cancel_at_period_end on the
     * subscription will be true. You can use this attribute to determine whether a subscription that has a status
     * of active is scheduled to be canceled at the end of the current period.
     */
    get willCancelAtPeriodEnd() {
        return this._subscription.cancel_at_period_end;
    }

    /**
     * If the subscription has been canceled, the date of that cancellation. If the subscription was canceled with
     * cancel_at_period_end, canceled_at will reflect the time of the most recent update request, not the end of the
     * subscription period when the subscription is automatically moved to a canceled state.
     */
    get cancellationDate() {
        return this._subscription.canceled_at ? new Date(this._subscription.canceled_at * 1000) : null;
    }

    get quantity() {
        const subscriptionItem = this._subscription.items.data[0];
        return subscriptionItem?.quantity ?? 0;
    }
    /**
     * If the subscription has ended, the date the subscription ended.  If a subscription has been canceled but there
     * is still time remaining, the date that the subscription will end.
     */
    get subscriptionEndDate() {
        if (this.willCancelAtPeriodEnd) return new Date(this._subscription.cancel_at * 1000);
        else if (this._subscription.ended_at > 0) return new Date(this._subscription.ended_at * 1000);
        else return null;
    }

    /**
     * Get the end date for the current trial period.
     */
    get trialEndDate() {
        return this._subscription.trial_end ? new Date(this._subscription.trial_end) : null;
    }

    /**
     * Has the subscription been fully canceled?
     */
    get isFullyCancelled() {
        return this.cancellationDate && this.subscriptionEndDate < new Date();
    }

    /**
     * Get the latest invoice.
     */
    get latestInvoice() {
        return new StripeInvoice(this._subscription.latest_invoice as Stripe.Invoice);
    }

    /**
     * Get the total monthly cost for this subscription, with taxes.
     */
    get monthlyCost() {
        const items = this._subscription.items?.data;
        const taxRate = 1 + this._subscription.default_tax_rates.reduce((tot, tr) => tot + (tr.percentage/100), 0);

        if (Array.isArray(items)) {
            return items.reduce((tot, si) => {
                return toTwoDecimals(tot + (taxRate * (si.price.unit_amount / 100) * si.quantity));
            }, 0) ?? 0;
        }
        else return 0;
    }
}

export default StripeSubscription;